import { palette } from "lib/miolo";
import Sparkline from "components/company/sections/summary/adornments/Sparkline";
import formatMoneyValueOnlyDigits from "components/utils/formatMoneyValueOnlyDigits";
import { getYear } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import filterUnionByTypename from "lib/utils/filterUnionByTypename";
import parseDateYmd from "lib/utils/parseDateYmd";
import { last } from "lodash";

interface ITotalCompensationsSummaryProps {
  totalCompensations: IBrazilianCompanyIssuerTopLevelFragment["totalCompensations"];
  financials: IBrazilianCompanyIssuerTopLevelFragment["financials"];
}

export default function TotalCompensationsSummary({ totalCompensations, financials }: ITotalCompensationsSummaryProps) {
  const { yearly } = financials;

  const yearlyBankFinancials = filterUnionByTypename(yearly, "YearlyBrazilianBankFinancialStatementRow");
  const yearlyCompanyFinancials = filterUnionByTypename(yearly, "YearlyBrazilianCompanyFinancialStatementRow");
  const yearlyInsurerFinancials = filterUnionByTypename(yearly, "YearlyBrazilianInsurerFinancialStatementRow");

  const isBank = yearlyBankFinancials.length > 0;
  const isCompany = yearlyCompanyFinancials.length > 0;
  const isInsurer = yearlyInsurerFinancials.length > 0;

  const topCompensation = last(
    [...totalCompensations]
      .sort((a, b) => {
        const aFiscalYear = getYear(parseDateYmd(a.fiscalYearEnd));
        const bFiscalYear = getYear(parseDateYmd(b.fiscalYearEnd));
        return aFiscalYear - bFiscalYear;
      })
      .filter((compensation) => {
        const compensationFiscalYearEnd = getYear(parseDateYmd(compensation.fiscalYearEnd));
        if (isBank) {
          return yearlyBankFinancials.some(
            ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
          );
        }
        if (isCompany) {
          return yearlyCompanyFinancials.some(
            ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
          );
        }
        if (isInsurer) {
          return yearlyInsurerFinancials.some(
            ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
          );
        }

        return false;
      }),
  );

  if (!topCompensation) {
    return null;
  }

  return (
    <div className="flex items-center justify-between space-x-2 xl:justify-start">
      <div className="font-aeonik">
        <h6 className="text-xl font-bold text-gray-700">
          R${formatMoneyValueOnlyDigits(topCompensation.totalCompensation)}
        </h6>
        <span className="text-sm font-medium text-gray-500">
          Total (reais) • {getYear(parseDateYmd(topCompensation.fiscalYearEnd))}
        </span>
      </div>

      {totalCompensations.length >= 3 && (
        <div>
          <Sparkline
            ariaLabel="Gráfico demonstrando a evolução do total gasto em Remuneração dos Administradores"
            chart={{
              margin: { top: 0, left: 0, bottom: 0, right: 0 },
              padding: { top: 4, left: 4, bottom: 4, right: 4 },
              primaryColor: palette.mariner,
              secondaryColor: palette.selago,
              width: 180,
              height: 40,
            }}
            nodes={totalCompensations.map((node) => node.totalCompensation)}
          />
        </div>
      )}
    </div>
  );
}
